import React, { useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../services/platformAPI';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useAltF1TestFill } from '../../hooks/useAltF1TestFill';
import { dummyWorkspaceSupportForm } from '../../utils/testFormDummies';
import {
  SUPPORT_CATEGORIES,
  SUPPORT_MODULES,
  SUPPORT_PRIORITIES,
  supportBasePath
} from '../../config/workspaceSupport';
import SupportTicketFileUpload from '../../components/support/SupportTicketFileUpload';
import './tender/CustomerMaster.scss';
import './tender/PersonnelPage.scss';
import './WorkspaceSupportPage.scss';

const INITIAL = {
  title: '',
  description: '',
  category: 'GENERAL',
  priority: 'MEDIUM',
  relatedModule: '',
  assigneeHint: ''
};

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const WorkspaceSupportNewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const listPath = supportBasePath(location.pathname);
  const [form, setForm] = useState(INITIAL);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [attachmentFiles, setAttachmentFiles] = useState([]);

  useDocumentTitle('New support ticket');
  useAltF1TestFill(true, () => setForm({ ...INITIAL, ...dummyWorkspaceSupportForm() }));

  const update = (name) => (event) => setForm((current) => ({ ...current, [name]: event.target.value }));
  const priorityLabel = SUPPORT_PRIORITIES.find((item) => item.value === form.priority)?.label || 'Medium';

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (!form.title.trim() || !form.description.trim()) {
      setError('Subject and description are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        priority: form.priority,
        relatedModule: form.relatedModule,
        assigneeHint: form.assigneeHint.trim() || undefined,
        status: 'OPEN'
      };
      if (attachmentFiles.length > 0) {
        const body = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== undefined && value !== '') body.append(key, value);
        });
        attachmentFiles.forEach((file) => body.append('attachments', file));
        await platformAPI.createSupportTicket(body);
      } else {
        await platformAPI.createSupportTicket(payload);
      }
      navigate(listPath, { replace: true, state: { created: true } });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create ticket');
      setSaving(false);
    }
  };

  return (
    <div className="cust-page ws-support ws-support--form">
      <form className="cust-record pers-record" onSubmit={submit}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate(listPath)}>
              <BiArrowBack />
              Support
            </button>
            <div className="cust-mast__row">
              <span className="cust-mark" aria-hidden>?</span>
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">Support</p>
                <h1>New ticket</h1>
                <div className="cust-mast__meta">
                  <span className="cust-chip">Open</span>
                  <span className="cust-chip">{priorityLabel} priority</span>
                  <span className="cust-chip">Number assigned on save</span>
                </div>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            <Button size="sm" variant="outline-primary" type="button" onClick={() => navigate(listPath)} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" type="submit" disabled={saving || !form.title.trim() || !form.description.trim()}>
              {saving ? 'Creating…' : 'Create ticket'}
            </Button>
          </div>
        </header>

        {error ? <Alert variant="danger" className="cust-record__alert">{error}</Alert> : null}

        <div className="cust-record__body">
          <div className="cust-record__main">
            <section className="cust-section">
              <header className="cust-section__head">
                <span>01</span>
                <div>
                  <h2>The request</h2>
                  <p>A short subject and enough detail for someone else to act on it.</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Subject" wide="12">
                  <Form.Control
                    value={form.title}
                    onChange={update('title')}
                    maxLength={200}
                    required
                    placeholder="Short summary of the issue or request"
                  />
                </Field>
                <Field label="Description" wide="12">
                  <Form.Control
                    as="textarea"
                    rows={7}
                    value={form.description}
                    onChange={update('description')}
                    maxLength={2000}
                    required
                    placeholder="What happened, what you expected, and the steps to see it again."
                  />
                </Field>
              </div>
            </section>

            <section className="cust-section">
              <header className="cust-section__head">
                <span>02</span>
                <div>
                  <h2>Routing</h2>
                  <p>Where this belongs, and how urgent it is. It opens as a new ticket.</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Category" wide="4">
                  <Form.Select value={form.category} onChange={update('category')} required>
                    {SUPPORT_CATEGORIES.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </Form.Select>
                </Field>
                <Field label="Priority" wide="4">
                  <Form.Select value={form.priority} onChange={update('priority')} required>
                    {SUPPORT_PRIORITIES.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </Form.Select>
                </Field>
                <Field label="Related module" wide="4">
                  <Form.Select value={form.relatedModule} onChange={update('relatedModule')}>
                    {SUPPORT_MODULES.map((item) => (
                      <option key={item.value || 'general'} value={item.value}>{item.label}</option>
                    ))}
                  </Form.Select>
                </Field>
                <Field label="Assigned to" wide="12">
                  <Form.Control
                    value={form.assigneeHint}
                    onChange={update('assigneeHint')}
                    placeholder="Optional. A person or team, if you already know who should take it."
                  />
                </Field>
              </div>
            </section>

            <section className="cust-section">
              <header className="cust-section__head">
                <span>03</span>
                <div>
                  <h2>Files</h2>
                  <p>Screenshots or documents that show the issue. Up to five files, 10 MB each.</p>
                </div>
              </header>
              <SupportTicketFileUpload
                files={attachmentFiles}
                onChange={setAttachmentFiles}
                disabled={saving}
              />
            </section>
          </div>
        </div>
      </form>
    </div>
  );
};

export default WorkspaceSupportNewPage;
